import { Component, OnDestroy, OnInit } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { combineLatest, of, Subscription } from 'rxjs';
import { catchError, startWith, switchMap, take, timeout } from 'rxjs/operators';
import { ChallengeDefinitionEntity } from '../../../meta-data/app-meta-data.service';
import { FitnessReferenceDataService } from '../../shared/services/fitness-reference-data.service';
import { RecipientRepositoryService, RecipientStory } from '../services/recipient-repository.service';

export interface RecipientSeason {
  id: number; name: string; label: string; date: string; current: boolean; recipients: RecipientStory[];
}
export function recipientSeasons(rows: RecipientStory[], current: ChallengeDefinitionEntity): RecipientSeason[] {
  const groups = new Map<number, RecipientSeason>();
  const add = (id: number, challenge: ChallengeDefinitionEntity) => {
    const isCurrent = id === current.ChallengeDefinitionId;
    const date = challenge?.ChallengeStartDate || '';
    const name = challenge?.Name || 'Recipients';
    const year = /^\d{4}/.exec(date)?.[0];
    groups.set(id, { id, name, label: isCurrent ? name : year || name, date, current: isCurrent, recipients: [] });
  };
  add(current.ChallengeDefinitionId, current);
  rows.forEach(row => {
    if (!groups.has(row.ChallengeDefinitionId)) { add(row.ChallengeDefinitionId, row.ChallengeDefinition); }
    groups.get(row.ChallengeDefinitionId).recipients.push(row);
  });
  return Array.from(groups.values()).sort((a, b) => Number(b.current) - Number(a.current) || b.date.localeCompare(a.date) || b.id - a.id);
}

@Component({ selector: 'app-recipients-page', templateUrl: './recipients-page.component.html', styleUrls: ['./recipients.scss'] })
export class RecipientsPageComponent implements OnInit, OnDestroy {
  previous = false; loading = true; error = '';
  groups: RecipientSeason[] = [];
  selected: RecipientSeason = null;
  currentChallenge: ChallengeDefinitionEntity;
  private listPosition: [number, number] = [0, 0];
  private subscription = new Subscription();
  constructor(private repository: RecipientRepositoryService, private route: ActivatedRoute, private reference: FitnessReferenceDataService,
    private router: Router, private viewport: ViewportScroller) {}
  get storyBase(): string { return this.previous ? '/recipients/previous' : '/recipients'; }
  selectSeason(group: RecipientSeason): void { this.selected = group; }
  restorePosition(): void { this.viewport.scrollToPosition(this.listPosition); }
  ngOnInit(): void {
    this.subscription.add(this.router.events.subscribe(event => {
      if (event instanceof NavigationStart && !this.route.firstChild && event.url.startsWith(this.storyBase + '/')) {
        this.listPosition = this.viewport.getScrollPosition();
      }
    }));
    this.subscription.add(combineLatest([this.route.data, this.repository.changes.pipe(startWith(null))]).pipe(
      switchMap(([data]) => {
        const childParams = this.route.firstChild?.snapshot.paramMap;
        const selectedId = this.selected?.id;
        this.previous = data.previous === true; this.loading = true; this.error = '';
        return combineLatest([this.repository.loadRecipients(), this.reference.getActiveChallenge().pipe(take(1))]).pipe(timeout(20000),
          switchMap(([rows, current]) => {
            const requested = selectedId || Number(childParams?.get('challengeId'))
              || rows.find(row => row.Slug === childParams?.get('slug'))?.ChallengeDefinitionId;
            this.currentChallenge = current;
            this.groups = recipientSeasons(rows, current);
            this.selected = this.groups.find(group => group.id === requested)
              || this.groups.find(group => group.current !== this.previous) || null;
            return of(null);
          }), catchError(() => { this.groups = []; this.error = 'Unable to load recipients. Please try again.'; return of(null); }));
      })).subscribe(() => { this.loading = false; }));
  }
  reload(): void { this.repository.refresh(); }
  ngOnDestroy(): void { this.subscription.unsubscribe(); }
}
